import { useEffect, useRef } from 'react';

/* ─────────────────────────────────────────────────────────────
   InfernoCanvas — scroll-driven particle system for the Nine
   Circles section.

   progress 0.00 – 0.55 : hellfire builds (crimson → orange peak)
   progress 0.55 – 0.75 : fire wanes, first ice crystals appear
   progress 0.75 – 1.00 : frozen lake — only falling hail, no fire
───────────────────────────────────────────────────────────────*/

/* ── helpers ── */
function lerp(a, b, t) { return a + (b - a) * t; }
function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
function rand(a, b) { return a + Math.random() * (b - a); }

function hexToRgb(hex) {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}
function lerpHex(h1, h2, t) {
  const [r1,g1,b1] = hexToRgb(h1);
  const [r2,g2,b2] = hexToRgb(h2);
  const r = Math.round(lerp(r1, r2, t)).toString(16).padStart(2,'0');
  const g = Math.round(lerp(g1, g2, t)).toString(16).padStart(2,'0');
  const b = Math.round(lerp(b1, b2, t)).toString(16).padStart(2,'0');
  return `#${r}${g}${b}`;
}

/* ── palettes ── */
const FIRE_PALETTE = ['#ff4500','#c0002a','#ff7010','#c9a84c','#ff2000'];
const ICE_PALETTE  = ['#a0c8ff','#c8e8ff','#80a8e0','#e8f4ff','#b0d8ff'];

function getPalette(iceBlend) {
  return FIRE_PALETTE.map((fc, i) => lerpHex(fc, ICE_PALETTE[i], iceBlend));
}

/* ── scroll progress ── */
// 0 = top of section just entering view, 1 = bottom of section leaving view
function getProgress(section) {
  const rect = section.getBoundingClientRect();
  const vh   = window.innerHeight;
  // progress: 0 when top of section is at bottom of viewport, 1 when bottom is at top
  const total = section.offsetHeight + vh;
  const gone  = vh - rect.top;
  return clamp(gone / total, 0, 1);
}

/* ── particle factory ── */
function makeParticle(cfg, w, h, respawn = false) {
  const dyMin = lerp(-0.55, 0.55, cfg.iceBlend);
  const dyMax = lerp( 0.15, 1.30, cfg.iceBlend);
  const dxSpread = lerp(0.45, 0.18, cfg.iceBlend);
  const palette  = cfg.palette;
  const color    = palette[Math.floor(Math.random() * palette.length)];
  const minR     = lerp(0.5, 0.7, cfg.iceBlend);
  const maxR     = lerp(2.6, 2.2, cfg.iceBlend);

  const dy = rand(dyMin, dyMax);

  // respawn from the appropriate edge
  let x = rand(0, w);
  let y;
  if (respawn) {
    y = dy < 0 ? h + 10 : -10;
  } else {
    y = rand(0, h);
  }

  return {
    x, y,
    r:    rand(minR, maxR),
    dx:   rand(-dxSpread, dxSpread),
    dy,
    targetDy: dy,
    wobble:      rand(0, Math.PI * 2),
    wobbleSpeed: rand(0.006, 0.022),
    wobbleAmp:   lerp(0.7, 0.2, cfg.iceBlend),
    alpha:   rand(0.05, cfg.maxAlpha * 0.8),
    fade:    rand(0.0008, 0.0018),
    fadeDir: 1,
    color,
    targetColor: color,
    maxAlpha: cfg.maxAlpha,
    glow: cfg.glow,
  };
}

export default function InfernoCanvas({ sectionRef }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas  = canvasRef.current;
    const section = sectionRef?.current;
    if (!canvas || !section) return;

    const ctx = canvas.getContext('2d');

    let w = canvas.width  = canvas.offsetWidth;
    let h = canvas.height = canvas.offsetHeight;

    // live config updated by scroll
    const cfg = {
      progress:  0,
      iceBlend:  0,
      intensity: 0.6,
      maxAlpha:  0.45,
      glow:      10,
      palette:   getPalette(0),
      count:     45,
    };

    function updateCfg(progress) {
      cfg.progress = progress;

      // ice blend: 0 until 0.55, then rises to 1 at 1.0
      cfg.iceBlend = clamp((progress - 0.55) / 0.45, 0, 1);

      // fire intensity: grows 0→0.45 then falls as ice takes over
      const peak = Math.sin(clamp(progress / 0.45, 0, 1) * Math.PI * 0.5);
      cfg.intensity = lerp(0.55, 1.0, peak) * lerp(1.0, 0.0, cfg.iceBlend);

      cfg.maxAlpha = lerp(0.42, 0.55, cfg.iceBlend)
                   * lerp(cfg.intensity, 0.55, cfg.iceBlend * 0.5);
      cfg.glow     = lerp(lerp(8, 14, peak * (1 - cfg.iceBlend)), 5, cfg.iceBlend);
      cfg.palette  = getPalette(cfg.iceBlend);
      cfg.count    = Math.round(lerp(lerp(42, 72, peak), 38, cfg.iceBlend * 0.7));
    }

    updateCfg(0);
    let particles = Array.from({ length: cfg.count }, () => makeParticle(cfg, w, h));

    let raf     = null;
    let running = false;

    function draw() {
      ctx.clearRect(0, 0, w, h);

      // grow/shrink pool to match cfg.count
      while (particles.length < cfg.count) {
        particles.push(makeParticle(cfg, w, h, true));
      }
      if (particles.length > cfg.count) {
        particles.splice(cfg.count);
      }

      for (const e of particles) {
        // smoothly nudge dy toward a target based on current iceBlend
        const dyMin = lerp(-0.55, 0.55, cfg.iceBlend);
        const dyMax = lerp( 0.15, 1.30, cfg.iceBlend);
        const midDy = (dyMin + dyMax) / 2;
        e.dy += (midDy - e.dy) * 0.004; // very gentle push

        // smoothly shift colour toward current palette
        const targetColor = cfg.palette[Math.floor(Math.random() * cfg.palette.length)];
        // only occasionally re-target colour so it doesn't flicker
        if (Math.random() < 0.003) e.targetColor = targetColor;
        // note: we just use the stored color as-is; new spawns will use new palette
        e.glow = cfg.glow;
        e.maxAlpha = cfg.maxAlpha;

        // alpha pulse
        e.alpha += e.fade * e.fadeDir;
        if (e.alpha >= e.maxAlpha) e.fadeDir = -1;
        if (e.alpha <= 0.02)       e.fadeDir =  1;

        e.wobble += e.wobbleSpeed;
        const wAmp = lerp(0.7, 0.18, cfg.iceBlend);
        e.x += e.dx + Math.sin(e.wobble) * wAmp;
        e.y += e.dy;

        // respawn
        const goesUp   = e.dy < 0;
        const offTop   = e.y < -10;
        const offBot   = e.y > h + 10;
        const offSide  = e.x < -10 || e.x > w + 10;
        const escaped  = (goesUp && offTop) || (!goesUp && offBot) || offSide;

        if (escaped) {
          const p = makeParticle(cfg, w, h, true);
          Object.assign(e, p);
        }

        ctx.save();
        ctx.globalAlpha = Math.max(0, e.alpha);
        ctx.shadowBlur  = e.glow;
        ctx.shadowColor = e.color;
        ctx.fillStyle   = e.color;
        ctx.beginPath();
        ctx.arc(e.x, e.y, e.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      raf = requestAnimationFrame(draw);
    }

    function start() { if (!running) { running = true; draw(); } }
    function stop()  { running = false; cancelAnimationFrame(raf); }

    // scroll handler
    const onScroll = () => updateCfg(getProgress(section));
    window.addEventListener('scroll', onScroll, { passive: true });

    // intersection observer — only run when visible
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting ? start() : stop(),
      { threshold: 0.02 }
    );
    observer.observe(canvas);

    const onResize = () => {
      w = canvas.width  = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
      particles = Array.from({ length: cfg.count }, () => makeParticle(cfg, w, h));
    };
    window.addEventListener('resize', onResize);

    return () => {
      stop();
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [sectionRef]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
      aria-hidden="true"
    />
  );
}
